import { useState, useEffect, useCallback, useRef } from 'react';
import request from '../../utils/request';
import { postGradminOrganizationsIdTopup } from '../../apis/api/admin';
import { useAuthStore, hasFunction } from '../../stores/authStore';
import EditOrganizationModal, {
  type OrganizationFormData,
} from '../../components/EditOrganizationModal/EditOrganizationModal';
import AutoTopupDebugModal from '../../components/AutoTopupDebugModal/AutoTopupDebugModal';
import OrganizationMembersModal from '../../components/OrganizationMembersModal/OrganizationMembersModal';
import Select from '../../components/Select/Select';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import DeleteOrganizationModal from '../../components/DeleteOrganizationModal/DeleteOrganizationModal';
import AlertModal from '../../components/AlertModal/AlertModal';
import { Button, Textarea } from '../../components/UI';
import { MobileResponsivePagination } from '../../components/MobileResponsivePagination';
import { MobileResponsiveTable } from '../../components/MobileResponsiveTable';

interface Organization {
  id: string;
  name: string;
  type: string;
  type_text: string;
  balance: number;
  payment_type: string;
  payment_text: string;
  status: number;
  status_text: string;
  cost_exposed: boolean;
  created_at: string;
  updated_at: string;
}

interface TopupFormData {
  amount: string;
  referenceId: string;
  notes: string;
}

interface GoodwillFormData {
  amount: string;
  notes: string;
}

interface ManualCreditFormData {
  creditType: 'purchased' | 'bonus' | 'goodwill';
  amount: string;
  reason: string;
  notes: string;
  operator: string;
}

export default function OrganizationListPage() {
  const fetchedRef = useRef(false);
  const prevParamsRef = useRef({ page: 1, pageSize: 10 });
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [keyword, setKeyword] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<{ id: string; name: string } | null>(null);
  const [isTopupModalOpen, setIsTopupModalOpen] = useState(false);
  const [topupOrg, setTopupOrg] = useState<Organization | null>(null);
  const [topupForm, setTopupForm] = useState<TopupFormData>({
    amount: '',
    referenceId: '',
    notes: '',
  });
  const [topupErrors, setTopupErrors] = useState<Partial<TopupFormData>>({});
  const [topupSubmitting, setTopupSubmitting] = useState(false);
  const [isGoodwillModalOpen, setIsGoodwillModalOpen] = useState(false);
  const [goodwillOrg, setGoodwillOrg] = useState<Organization | null>(null);
  const [goodwillForm, setGoodwillForm] = useState<GoodwillFormData>({
    amount: '',
    notes: '',
  });
  const [goodwillErrors, setGoodwillErrors] = useState<Partial<GoodwillFormData>>({});
  const [goodwillSubmitting, setGoodwillSubmitting] = useState(false);
  const [isManualCreditModalOpen, setIsManualCreditModalOpen] = useState(false);
  const [manualCreditOrg, setManualCreditOrg] = useState<Organization | null>(null);
  // 自动充值并发锁调试面板（仅 auto_topup_debug_manage 权限可见）
  const [isAutoTopupDebugOpen, setIsAutoTopupDebugOpen] = useState(false);
  const [autoTopupDebugOrg, setAutoTopupDebugOrg] = useState<Organization | null>(null);
  const [manualCreditForm, setManualCreditForm] = useState<ManualCreditFormData>({
    creditType: 'purchased',
    amount: '',
    reason: '',
    notes: '',
    operator: '',
  });
  const [manualCreditErrors, setManualCreditErrors] = useState<Partial<ManualCreditFormData>>({});
  const [manualCreditSubmitting, setManualCreditSubmitting] = useState(false);

  // 权限检查
  const canCreate = hasFunction(user, 'organization_create');
  const canEdit = hasFunction(user, 'organization_edit');
  const canDelete = hasFunction(user, 'organization_delete');
  const canViewMembers = hasFunction(user, 'organization_view_members');
  const canToggleStatus = hasFunction(user, 'organization_toggle_status');
  const canDebugAutoTopup = hasFunction(user, 'auto_topup_debug_manage');

  // Delete organization modal state
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    org: Organization | null;
    loading: boolean;
  }>({
    isOpen: false,
    org: null,
    loading: false,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    content: string;
    type: 'warning' | 'danger' | 'info';
    loading: boolean;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    content: '',
    type: 'warning',
    loading: false,
    onConfirm: () => {},
  });

  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    title: string;
    content: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    content: '',
    type: 'info',
  });

  // Fetch organization list
  const fetchOrganizations = useCallback(
    async (searchKeyword?: string) => {
      setLoading(true);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response: any = await request.get('/gradmin/organizations', {
          params: {
            page: pagination.page,
            page_size: pagination.pageSize,
            keyword: searchKeyword ?? keyword ?? undefined,
          },
        });

        if (response.code === 0) {
          setOrganizations(response.data.items);
          setPagination(prev => ({
            ...prev,
            total: response.data.total,
          }));
        }
      } catch (error) {
        console.error('Failed to fetch organization list:', error);
      } finally {
        setLoading(false);
      }
    },
    [pagination.page, pagination.pageSize, keyword]
  );

  // Initial load and re-fetch on pagination change (keyword changes don't trigger)
  useEffect(() => {
    const currentParams = { page: pagination.page, pageSize: pagination.pageSize };
    const paramsChanged =
      prevParamsRef.current.page !== currentParams.page ||
      prevParamsRef.current.pageSize !== currentParams.pageSize;

    if (!paramsChanged && fetchedRef.current) {
      // Params unchanged and already fetched, skip (prevent StrictMode duplicate requests)
      return;
    }

    prevParamsRef.current = currentParams;
    fetchedRef.current = true;
    fetchOrganizations();
  }, [pagination.page, pagination.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search handler
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchOrganizations(keyword);
  };

  // Search on Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Pagination handler
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPagination(prev => ({ ...prev, page: 1, pageSize }));
  };

  // Calculate total pages
  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  // Format balance display
  const formatBalance = (balance: number) => {
    return (balance / 1000000).toFixed(2);
  };

  const resetTopupState = () => {
    setIsTopupModalOpen(false);
    setTopupOrg(null);
    setTopupForm({ amount: '', referenceId: '', notes: '' });
    setTopupErrors({});
    setTopupSubmitting(false);
  };

  const resetGoodwillState = () => {
    setIsGoodwillModalOpen(false);
    setGoodwillOrg(null);
    setGoodwillForm({ amount: '', notes: '' });
    setGoodwillErrors({});
    setGoodwillSubmitting(false);
  };

  const handleOpenTopupModal = (org: Organization) => {
    if (org.payment_type !== 'prepaid') return;
    setTopupOrg(org);
    setIsTopupModalOpen(true);
    setTopupForm({ amount: '', referenceId: '', notes: '' });
    setTopupErrors({});
  };

  const handleTopupChange = (field: keyof TopupFormData, value: string) => {
    setTopupForm(prev => ({ ...prev, [field]: value }));
    if (topupErrors[field]) {
      setTopupErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleOpenGoodwillModal = (org: Organization) => {
    setGoodwillOrg(org);
    setIsGoodwillModalOpen(true);
    setGoodwillForm({ amount: '', notes: '' });
    setGoodwillErrors({});
  };

  const handleGoodwillChange = (field: keyof GoodwillFormData, value: string) => {
    setGoodwillForm(prev => ({ ...prev, [field]: value }));
    if (goodwillErrors[field]) {
      setGoodwillErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleOpenManualCreditModal = (org: Organization) => {
    setManualCreditOrg(org);
    setManualCreditForm({
      creditType: 'purchased',
      amount: '',
      reason: '',
      notes: '',
      operator: user?.email || user?.name || '',
    });
    setManualCreditErrors({});
    setIsManualCreditModalOpen(true);
  };

  const resetManualCreditState = () => {
    setIsManualCreditModalOpen(false);
    setManualCreditOrg(null);
    setManualCreditSubmitting(false);
    setManualCreditErrors({});
    setManualCreditForm({
      creditType: 'purchased',
      amount: '',
      reason: '',
      notes: '',
      operator: '',
    });
  };

  const handleOpenAutoTopupDebug = (org: Organization) => {
    setAutoTopupDebugOrg(org);
    setIsAutoTopupDebugOpen(true);
  };

  const handleCloseAutoTopupDebug = () => {
    setIsAutoTopupDebugOpen(false);
    setAutoTopupDebugOrg(null);
  };

  const handleManualCreditChange = (field: keyof ManualCreditFormData, value: string) => {
    setManualCreditForm(prev => ({ ...prev, [field]: value }));
    if (manualCreditErrors[field]) {
      setManualCreditErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateManualCredit = () => {
    const errors: Partial<ManualCreditFormData> = {};
    const amount = Number(manualCreditForm.amount);
    if (!manualCreditForm.amount.trim() || Number.isNaN(amount) || amount <= 0) {
      errors.amount = 'Please enter a valid amount greater than 0';
    }
    if (!manualCreditForm.reason.trim()) {
      errors.reason = 'Please enter reason';
    }
    setManualCreditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleManualCreditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualCreditOrg) return;
    if (!validateManualCredit()) return;

    const amountMicros = Math.round(Number(manualCreditForm.amount) * 1000000);

    setManualCreditSubmitting(true);
    try {
      await request.post(`/gradmin/organizations/${manualCreditOrg.id}/manual-credit`, {
        amount_micros: amountMicros,
        credit_type: manualCreditForm.creditType,
        reason: manualCreditForm.reason.trim(),
        notes: manualCreditForm.notes.trim(),
        operator: manualCreditForm.operator.trim(),
      });

      resetManualCreditState();
      fetchOrganizations();
      showAlert('Manual Credit Submitted', 'Manual credit granted successfully.', 'success');
    } catch (error) {
      showAlert(
        'Manual Credit Failed',
        error instanceof Error ? error.message : 'Manual credit request failed',
        'error'
      );
      setManualCreditSubmitting(false);
    }
  };

  const validateTopup = () => {
    const errors: Partial<TopupFormData> = {};
    const amount = Number(topupForm.amount);
    if (!topupForm.amount.trim() || Number.isNaN(amount) || amount <= 0) {
      errors.amount = 'Please enter a valid amount greater than 0';
    }
    if (!topupForm.referenceId.trim()) {
      errors.referenceId = 'Please enter reference ID';
    }
    setTopupErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateGoodwill = () => {
    const errors: Partial<GoodwillFormData> = {};
    const amount = Number(goodwillForm.amount);
    if (!goodwillForm.amount.trim() || Number.isNaN(amount) || amount <= 0) {
      errors.amount = 'Please enter a valid amount greater than 0';
    }
    if (!goodwillForm.notes.trim()) {
      errors.notes = 'Please enter notes';
    }
    setGoodwillErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const showAlert = (
    title: string,
    content: string,
    type: 'success' | 'error' | 'warning' | 'info' = 'info'
  ) => {
    setAlertModal({
      isOpen: true,
      title,
      content,
      type,
    });
  };

  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleTopupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!topupOrg) return;
    if (!validateTopup()) return;

    const amountMicros = Math.round(Number(topupForm.amount) * 1000000);
    const referenceId = topupForm.referenceId.trim();
    const notes = topupForm.notes.trim();

    setTopupSubmitting(true);
    try {
      await postGradminOrganizationsIdTopup(
        { id: topupOrg.id },
        {
          amount_micros: amountMicros,
          reference_id: referenceId,
          created_by: user?.email || user?.name || 'admin_web',
          notes,
        }
      );

      resetTopupState();
      fetchOrganizations();
      showAlert('Topup Submitted', 'Topup request submitted successfully.', 'success');
    } catch (error) {
      showAlert(
        'Topup Failed',
        error instanceof Error ? error.message : 'Topup request failed',
        'error'
      );
      setTopupSubmitting(false);
    }
  };

  const handleGoodwillSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!goodwillOrg) return;
    if (!validateGoodwill()) return;

    const amountMicros = Math.round(Number(goodwillForm.amount) * 1000000);
    const notes = goodwillForm.notes.trim();

    setGoodwillSubmitting(true);
    try {
      await request.post(`/gradmin/organizations/${goodwillOrg.id}/goodwill`, {
        amount_micros: amountMicros,
        notes,
      });

      resetGoodwillState();
      fetchOrganizations();
      showAlert('Goodwill Submitted', 'Goodwill credit granted successfully.', 'success');
    } catch (error) {
      showAlert(
        'Goodwill Failed',
        error instanceof Error ? error.message : 'Goodwill request failed',
        'error'
      );
      setGoodwillSubmitting(false);
    }
  };

  // Open add modal
  const handleOpenModal = () => {
    setEditingOrg(null);
    setIsModalOpen(true);
  };

  // Open edit modal
  const handleEdit = (org: Organization) => {
    setEditingOrg(org);
    setIsModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingOrg(null);
  };

  // Open view members modal
  const handleViewMembers = (org: Organization) => {
    setSelectedOrg({ id: org.id, name: org.name });
    setIsMembersModalOpen(true);
  };

  // Close view members modal
  const handleCloseMembersModal = () => {
    setIsMembersModalOpen(false);
    setSelectedOrg(null);
  };

  // Submit form (add or edit)
  const handleSubmit = async (formData: OrganizationFormData) => {
    if (editingOrg) {
      // Edit mode
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any = await request.put(`/gradmin/organizations/${editingOrg.id}`, formData);
      if (response.code === 0) {
        fetchOrganizations();
      }
    } else {
      // Add mode
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any = await request.post('/gradmin/organizations', formData);
      if (response.code === 0) {
        fetchOrganizations();
      }
    }
  };

  // Show confirm modal
  const showConfirm = (
    title: string,
    content: string,
    type: 'warning' | 'danger' | 'info',
    onConfirm: () => void
  ) => {
    setConfirmModal({
      isOpen: true,
      title,
      content,
      type,
      loading: false,
      onConfirm,
    });
  };

  // Close confirm modal
  const closeConfirm = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false, loading: false }));
  };

  // Set confirm modal loading state
  const setConfirmLoading = (loading: boolean) => {
    setConfirmModal(prev => ({ ...prev, loading }));
  };

  // Update organization status (enable/disable)
  const handleUpdateStatus = (org: Organization, status: number) => {
    const isEnable = status === 1;
    showConfirm(
      isEnable ? 'Enable Organization' : 'Disable Organization',
      `Are you sure you want to ${isEnable ? 'enable' : 'disable'} organization "${org.name}"?`,
      'warning',
      async () => {
        try {
          setConfirmLoading(true);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const response: any = await request.patch(`/gradmin/organizations/${org.id}/status`, {
            status,
          });
          if (response.code === 0) {
            fetchOrganizations();
          }
          closeConfirm();
        } catch (error) {
          closeConfirm();
          console.error('Operation failed:', error);
        }
      }
    );
  };

  // Toggle whether the gateway returns cost in video task responses for this org
  const handleUpdateCostExposed = (org: Organization, costExposed: boolean) => {
    showConfirm(
      costExposed ? 'Enable Cost Exposure' : 'Disable Cost Exposure',
      `Are you sure you want to ${costExposed ? 'enable' : 'disable'} cost exposure for organization "${org.name}"?`,
      'warning',
      async () => {
        try {
          setConfirmLoading(true);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const response: any = await request.patch(
            `/gradmin/organizations/${org.id}/cost-exposed`,
            { cost_exposed: costExposed }
          );
          if (response.code === 0) {
            fetchOrganizations();
          }
          closeConfirm();
        } catch (error) {
          closeConfirm();
          showAlert(
            'Update Failed',
            error instanceof Error ? error.message : 'Failed to update cost exposure setting',
            'error'
          );
        }
      }
    );
  };

  // Delete organization
  const handleDelete = (org: Organization) => {
    setDeleteModal({ isOpen: true, org, loading: false });
  };

  // Execute delete
  const handleDeleteConfirm = async () => {
    if (!deleteModal.org) return;
    setDeleteModal(prev => ({ ...prev, loading: true }));
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any = await request.delete(`/gradmin/organizations/${deleteModal.org.id}`);
      if (response.code === 0) {
        setDeleteModal({ isOpen: false, org: null, loading: false });
        fetchOrganizations();
      } else {
        setDeleteModal(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      setDeleteModal(prev => ({ ...prev, loading: false }));
      console.error('Delete failed:', error);
    }
  };

  // Close delete modal
  const handleDeleteCancel = () => {
    if (deleteModal.loading) return;
    setDeleteModal({ isOpen: false, org: null, loading: false });
  };

  return (
    <div className="min-h-full">
      <div className="page-header">
        <h1 className="page-title">Organization List</h1>
      </div>

      {/* Search bar */}
      <div className="page-search-bar">
        <div className="page-search-input-wrapper">
          <input
            type="text"
            className="page-search-input"
            placeholder="Search by organization name"
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {keyword && (
            <button
              className="page-search-clear-btn"
              onClick={() => {
                setKeyword('');
                setPagination(prev => ({ ...prev, page: 1 }));
                fetchOrganizations('');
              }}
              title="Clear"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          )}
        </div>
        <button className="page-search-button" onClick={handleSearch}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          Search
        </button>
        {canCreate && (
          <button className="page-add-button" onClick={handleOpenModal}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add Organization
          </button>
        )}
      </div>

      {/* Data table */}
      <MobileResponsiveTable
        containerClassName="page-table-container"
        tableClassName="page-data-table"
      >
        <thead>
          <tr>
            <th>Organization Name</th>
            <th>Type</th>
            <th>Payment Type</th>
            <th>Balance</th>
            <th>Status</th>
            <th>Cost Exposed</th>
            <th>Created At</th>
            <th>Updated At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={9} className="page-loading-cell">
                <div className="page-loading-spinner"></div>
                Loading...
              </td>
            </tr>
          ) : organizations.length === 0 ? (
            <tr>
              <td colSpan={9} className="page-empty-cell">
                No data
              </td>
            </tr>
          ) : (
            organizations.map(org => (
              <tr key={org.id}>
                <td>{org.name}</td>
                <td>
                  <span className={`page-tag page-tag-${org.type}`}>{org.type_text}</span>
                </td>
                <td>
                  <span className={`page-tag page-tag-payment-${org.payment_type}`}>
                    {org.payment_text}
                  </span>
                </td>
                <td>${formatBalance(org.balance)}</td>
                <td>
                  <span
                    className={`page-status-badge page-status-${org.status === 1 ? 'active' : 'inactive'}`}
                  >
                    {org.status_text}
                  </span>
                </td>
                <td>
                  <span
                    className={`page-status-badge page-status-${org.cost_exposed ? 'active' : 'inactive'}`}
                  >
                    {org.cost_exposed ? 'Exposed' : 'Hidden'}
                  </span>
                </td>
                <td>{org.created_at}</td>
                <td>{org.updated_at}</td>
                <td>
                  <div className="page-action-buttons">
                    {canViewMembers && (
                      <button
                        className="page-action-btn page-action-view"
                        onClick={() => handleViewMembers(org)}
                      >
                        View Members
                      </button>
                    )}
                    {canToggleStatus &&
                      (org.status === 2 ? (
                        <button
                          className="page-action-btn page-action-enable"
                          onClick={() => handleUpdateStatus(org, 1)}
                        >
                          Enable
                        </button>
                      ) : (
                        <button
                          className="page-action-btn page-action-disable"
                          onClick={() => handleUpdateStatus(org, 2)}
                        >
                          Disable
                        </button>
                      ))}
                    {canEdit && (
                      <button
                        className="page-action-btn page-action-edit"
                        onClick={() => handleEdit(org)}
                      >
                        Edit
                      </button>
                    )}
                    {canEdit &&
                      (org.cost_exposed ? (
                        <button
                          className="page-action-btn page-action-disable"
                          onClick={() => handleUpdateCostExposed(org, false)}
                        >
                          Hide Cost
                        </button>
                      ) : (
                        <button
                          className="page-action-btn page-action-enable"
                          onClick={() => handleUpdateCostExposed(org, true)}
                        >
                          Expose Cost
                        </button>
                      ))}
                    <button
                      className="page-action-btn bg-sky-100 text-sky-700 hover:enabled:bg-sky-200"
                      onClick={() => handleOpenManualCreditModal(org)}
                      title="Manual credit for purchased/bonus/goodwill"
                    >
                      Manual Credit
                    </button>
                    {canDebugAutoTopup && (
                      <button
                        className="page-action-btn bg-indigo-100 text-indigo-700 hover:enabled:bg-indigo-200"
                        onClick={() => handleOpenAutoTopupDebug(org)}
                        title="Preset / view auto top-up concurrency lock for testing"
                      >
                        Debug Lock
                      </button>
                    )}
                    <button
                      className="page-action-btn bg-blue-100 text-blue-700 hover:enabled:bg-blue-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                      onClick={() => handleOpenTopupModal(org)}
                      disabled={org.payment_type !== 'prepaid'}
                      title={
                        org.payment_type === 'prepaid'
                          ? 'Top up this organization'
                          : 'Topup is disabled for postpaid organizations'
                      }
                    >
                      Topup
                    </button>
                    <button
                      className="page-action-btn bg-emerald-100 text-emerald-700 hover:enabled:bg-emerald-200"
                      onClick={() => handleOpenGoodwillModal(org)}
                      title="Grant goodwill credits"
                    >
                      Goodwill
                    </button>
                    {canDelete && (
                      <button
                        className="page-action-btn page-action-delete"
                        onClick={() => handleDelete(org)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </MobileResponsiveTable>

      {/* Add/Edit organization modal */}
      <EditOrganizationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        editingOrg={editingOrg}
      />

      {/* View members modal */}
      <OrganizationMembersModal
        isOpen={isMembersModalOpen}
        onClose={handleCloseMembersModal}
        organization={selectedOrg}
      />

      {/* Auto top-up concurrency lock debug modal */}
      <AutoTopupDebugModal
        isOpen={isAutoTopupDebugOpen}
        organizationId={autoTopupDebugOrg?.id ?? ''}
        orgName={autoTopupDebugOrg?.name ?? ''}
        onClose={handleCloseAutoTopupDebug}
      />

      {/* Delete organization modal */}
      <DeleteOrganizationModal
        isOpen={deleteModal.isOpen}
        orgName={deleteModal.org?.name ?? ''}
        loading={deleteModal.loading}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Confirm modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        content={confirmModal.content}
        type={confirmModal.type}
        loading={confirmModal.loading}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
      />

      {/* Topup modal */}
      {isTopupModalOpen && topupOrg && (
        <div className="modal-overlay" onClick={resetTopupState}>
          <div className="modal-content max-w-140" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Topup Organization Balance</h2>
              <button className="modal-close" onClick={resetTopupState}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleTopupSubmit}>
              <div className="modal-body">
                <div className="mb-3.5 text-sm text-gray-700">Organization: {topupOrg.name}</div>

                <div className="blue-modal-form-group">
                  <label className="blue-modal-form-label" htmlFor="topup-amount">
                    Amount (USD)
                  </label>
                  <input
                    id="topup-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    className={`blue-modal-form-input ${topupErrors.amount ? 'error' : ''}`}
                    placeholder="Enter amount in USD"
                    value={topupForm.amount}
                    onChange={e => handleTopupChange('amount', e.target.value)}
                  />
                  {topupErrors.amount && (
                    <span className="blue-modal-error-msg">{topupErrors.amount}</span>
                  )}
                </div>

                <div className="blue-modal-form-group">
                  <label className="blue-modal-form-label" htmlFor="topup-reference-id">
                    Reference ID
                  </label>
                  <input
                    id="topup-reference-id"
                    type="text"
                    className={`blue-modal-form-input ${topupErrors.referenceId ? 'error' : ''}`}
                    placeholder="Enter reference ID"
                    value={topupForm.referenceId}
                    onChange={e => handleTopupChange('referenceId', e.target.value)}
                  />
                  {topupErrors.referenceId && (
                    <span className="blue-modal-error-msg">{topupErrors.referenceId}</span>
                  )}
                </div>

                <div className="blue-modal-form-group">
                  <label className="blue-modal-form-label" htmlFor="topup-notes">
                    Notes
                  </label>
                  <Textarea
                    id="topup-notes"
                    className="blue-modal-form-input"
                    placeholder="Enter notes"
                    value={topupForm.notes}
                    onChange={e => handleTopupChange('notes', e.target.value)}
                    rows={4}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <Button
                  type="button"
                  className="blue-modal-btn blue-modal-btn-secondary"
                  disabled={topupSubmitting}
                  onClick={resetTopupState}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="blue-modal-btn blue-modal-btn-primary"
                  loading={topupSubmitting}
                >
                  Confirm
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Goodwill modal */}
      {isGoodwillModalOpen && goodwillOrg && (
        <div className="modal-overlay" onClick={resetGoodwillState}>
          <div className="modal-content max-w-140" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Grant Goodwill Credit</h2>
              <button className="modal-close" onClick={resetGoodwillState}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleGoodwillSubmit}>
              <div className="modal-body">
                <div className="mb-3.5 text-sm text-gray-700">Organization: {goodwillOrg.name}</div>

                <div className="blue-modal-form-group">
                  <label className="blue-modal-form-label" htmlFor="goodwill-amount">
                    Amount (USD)
                  </label>
                  <input
                    id="goodwill-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    className={`blue-modal-form-input ${goodwillErrors.amount ? 'error' : ''}`}
                    placeholder="Enter amount in USD"
                    value={goodwillForm.amount}
                    onChange={e => handleGoodwillChange('amount', e.target.value)}
                  />
                  {goodwillErrors.amount && (
                    <span className="blue-modal-error-msg">{goodwillErrors.amount}</span>
                  )}
                </div>

                <div className="blue-modal-form-group">
                  <label className="blue-modal-form-label" htmlFor="goodwill-notes">
                    Notes
                  </label>
                  <Textarea
                    id="goodwill-notes"
                    className={`blue-modal-form-input ${goodwillErrors.notes ? 'error' : ''}`}
                    placeholder="Enter compensation reason"
                    value={goodwillForm.notes}
                    onChange={e => handleGoodwillChange('notes', e.target.value)}
                    rows={4}
                  />
                  {goodwillErrors.notes && (
                    <span className="blue-modal-error-msg">{goodwillErrors.notes}</span>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <Button
                  type="button"
                  className="blue-modal-btn blue-modal-btn-secondary"
                  disabled={goodwillSubmitting}
                  onClick={resetGoodwillState}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="blue-modal-btn blue-modal-btn-primary"
                  loading={goodwillSubmitting}
                >
                  Confirm
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manual credit modal */}
      {isManualCreditModalOpen && manualCreditOrg && (
        <div className="modal-overlay" onClick={resetManualCreditState}>
          <div className="modal-content max-w-140" onClick={event => event.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Manual Credit</h2>
              <button className="modal-close" onClick={resetManualCreditState}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleManualCreditSubmit}>
              <div className="modal-body">
                <div className="mb-3.5 text-sm text-gray-700">
                  Organization: {manualCreditOrg.name}
                </div>

                <div className="blue-modal-form-group">
                  <label className="blue-modal-form-label" htmlFor="manual-credit-type">
                    Credit Type
                  </label>
                  <Select
                    value={manualCreditForm.creditType}
                    options={[
                      { value: 'purchased', label: 'Purchased' },
                      { value: 'bonus', label: 'Bonus' },
                      { value: 'goodwill', label: 'Goodwill' },
                    ]}
                    onChange={value => {
                      const nextValue = String(value);
                      if (
                        nextValue === 'purchased' ||
                        nextValue === 'bonus' ||
                        nextValue === 'goodwill'
                      ) {
                        handleManualCreditChange('creditType', nextValue);
                      }
                    }}
                    className="w-full"
                  />
                </div>

                <div className="blue-modal-form-group">
                  <label className="blue-modal-form-label" htmlFor="manual-credit-amount">
                    Amount (USD)
                  </label>
                  <input
                    id="manual-credit-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    className={`blue-modal-form-input ${manualCreditErrors.amount ? 'error' : ''}`}
                    placeholder="Enter amount in USD"
                    value={manualCreditForm.amount}
                    onChange={event => handleManualCreditChange('amount', event.target.value)}
                  />
                  {manualCreditErrors.amount && (
                    <span className="blue-modal-error-msg">{manualCreditErrors.amount}</span>
                  )}
                </div>

                <div className="blue-modal-form-group">
                  <label className="blue-modal-form-label" htmlFor="manual-credit-reason">
                    Reason
                  </label>
                  <input
                    id="manual-credit-reason"
                    type="text"
                    className={`blue-modal-form-input ${manualCreditErrors.reason ? 'error' : ''}`}
                    placeholder="Example: offline invoice settlement"
                    value={manualCreditForm.reason}
                    onChange={event => handleManualCreditChange('reason', event.target.value)}
                  />
                  {manualCreditErrors.reason && (
                    <span className="blue-modal-error-msg">{manualCreditErrors.reason}</span>
                  )}
                </div>

                <div className="blue-modal-form-group">
                  <label className="blue-modal-form-label" htmlFor="manual-credit-operator">
                    Operator
                  </label>
                  <input
                    id="manual-credit-operator"
                    type="text"
                    className="blue-modal-form-input"
                    placeholder="operator id/email"
                    value={manualCreditForm.operator}
                    onChange={event => handleManualCreditChange('operator', event.target.value)}
                  />
                </div>

                <div className="blue-modal-form-group">
                  <label className="blue-modal-form-label" htmlFor="manual-credit-notes">
                    Notes
                  </label>
                  <Textarea
                    id="manual-credit-notes"
                    className="blue-modal-form-input"
                    placeholder="Optional notes"
                    value={manualCreditForm.notes}
                    onChange={event => handleManualCreditChange('notes', event.target.value)}
                    rows={4}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <Button
                  type="button"
                  className="blue-modal-btn blue-modal-btn-secondary"
                  disabled={manualCreditSubmitting}
                  onClick={resetManualCreditState}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="blue-modal-btn blue-modal-btn-primary"
                  loading={manualCreditSubmitting}
                >
                  Confirm
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        content={alertModal.content}
        type={alertModal.type}
        confirmText="OK"
        onConfirm={closeAlert}
      />

      {/* Pagination */}
      {pagination.total > 0 && (
        <>
          <div className="page-pagination max-sm:hidden">
            <div className="page-pagination-info">
              Total {pagination.total} records, per page
              <Select
                value={pagination.pageSize}
                options={[
                  { value: 10, label: '10' },
                  { value: 20, label: '20' },
                  { value: 50, label: '50' },
                ]}
                onChange={value => handlePageSizeChange(Number(value))}
                className="page-size-select"
              />
              records
            </div>
            <div className="page-pagination-buttons">
              <button
                className="page-pagination-btn"
                disabled={pagination.page === 1}
                onClick={() => handlePageChange(pagination.page - 1)}
              >
                Previous
              </button>
              <span className="page-pagination-current">
                {pagination.page} / {totalPages}
              </span>
              <button
                className="page-pagination-btn"
                disabled={pagination.page >= totalPages}
                onClick={() => handlePageChange(pagination.page + 1)}
              >
                Next
              </button>
            </div>
          </div>

          <MobileResponsivePagination
            current={pagination.page}
            total={pagination.total}
            pageSize={pagination.pageSize}
            onChange={handlePageChange}
          />
        </>
      )}
    </div>
  );
}
